/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { InterviewForm } from 'InterviewForm/InterviewForm';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { interviewActions } from '_actions';
import { getInterviewForm } from '_helpers/reduxSelector';
import { latestInterviewFormVersion, dateTimeConstants } from '_constants';
import ReferenceContainer from './_components/ReferenceContainer';
import ReferenceQuestionnaire from './_components/ReferenceQuestionnaire';
import { CustomDateInput } from '_components/CustomDateInput';
import NumberFormat from 'react-number-format';
import { preventNegativeValueInDocs } from '_helpers/helpers';
import PropTypes from "prop-types";

const DATE_FORMAT = dateTimeConstants.DATE_FORMAT;

// Common validation rules
const noteRule = yup.string().max(350, 'Must be less than 350 characters');
const questionRule = yup
  .string()
  .max(200, 'Must be less 200 characters')
  .matches(/^\s*\S.*$|^$/, '* This field cannot contain only blankspaces');
const employerRule = yup
  .string()
  .nullable()
  .max(100, 'Must be less 100 characters');

const refInterviewSchema = yup.object().shape({
  // Note fields (ref_*_notes)
  ...Object.fromEntries(
    ['ref_1_notes', 'ref_2_notes', 'ref_5_notes', 'ref_6_notes', 'desc'].map(
      (key) => [key, noteRule],
    ),
  ),

  // Question fields (hire_*, work_ethic_*, consistency_*)
  ...Object.fromEntries(
    [
      'confirm_dates_1',
      'confirm_dates_2',
      'confirm_dates_5',
      'confirm_dates_6',
      'hire_1',
      'hire_2',
      'hire_5',
      'hire_6',
      'key_strengths_1',
      'key_strengths_2',
      'key_strengths_5',
      'key_strengths_6',
      'consistency_1',
      'consistency_2',
      'consistency_5',
      'consistency_6',
      'work_ethic_1',
      'work_ethic_2',
      'work_ethic_5',
      'work_ethic_6',
      'prior_knowledge_1',
      'prior_knowledge_2',
      'prior_knowledge_5',
      'prior_knowledge_6',
      'reason_good_fit_1',
      'reason_good_fit_2',
      'reason_good_fit_5',
      'reason_good_fit_6',
    ].map((key) => [key, questionRule]),
  ),

  ...Object.fromEntries(
    [
      'work_reference_6_employer',
      'work_reference_6_position',
      'work_reference_6_supervisor',
    ].map((key) => [key, employerRule]),
  ),
});

const ReferenceForm = (props) => {
  const {
    hash,
    formId,
    setFormId,
    user,
    applicant,
    setCurrentView,
    isNew,
    setIsNew,
    newFormId,
    setNewFormId,
    setIsSubmittable,
    isSubmittable,
  } = props;
  const interviewForm = useSelector((state) => getInterviewForm(state));

  const dispatch = useDispatch();

  const [key, setKey] = useState('referenceForm');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isRefsOk, setIsRefsOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasWarnedRef = useRef(false);

  const {
    reset,
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(refInterviewSchema),
    defaultValues: {
      verified_check_1: false,
      ref_1_notes: '',
      verified_1_date: '',
      verified_check_2: false,
      ref_2_notes: '',
      verified_2_date: '',
      verified_check_5: false,
      ref_3_notes: '',
      verified_3_date: '',
      verified_check_6: false,
      ref_4_notes: '',
      verified_4_date: '',
      // --------------------------------//
      confirm_dates_1: '',
      key_strengths_1: '',
      prior_knowledge_1: '',
      reason_good_fit_1: '',
      hire_1: '',
      work_ethic_1: '',
      consistency_1: '',
      confirm_dates_2: '',
      key_strengths_2: '',
      prior_knowledge_2: '',
      reason_good_fit_2: '',

      hire_2: '',
      work_ethic_2: '',
      consistency_2: '',
      // --------------------------------//
      facebook: false,
      facebook_date: '',
      instagram: false,
      instagram_date: '',
      desc: '',
    },
  });

  const fetchInterviewForm = () => {
    dispatch(interviewActions.getInterviewForm(formId));
  };

  useEffect(() => {
    if (!isNew) {
      fetchInterviewForm();
    }
  }, []);

  const onSubmit = (data) => {
    setLoading(true);
    const form = {
      work_reference_1_employer: applicant?.reference1?.last_employee ?? '',
      work_reference_1_position: applicant?.reference1?.position ?? '',
      work_reference_1_supervisor: applicant?.reference1?.supervisor_name ?? '',
      work_reference_1_phone: applicant?.reference1?.telephone ?? '',
      work_reference_1_verified: verified_check_1 ? 1 : 0,
      work_reference_1_notes: data.ref_1_notes ?? '',
      work_reference_1_date_verified: data.verified_1_date?._d
        ? moment(data.verified_1_date._d).format(DATE_FORMAT)
        : '',

      work_reference_2_employer: applicant?.reference2?.last_employee ?? '',
      work_reference_2_position: applicant?.reference2?.position ?? '',
      work_reference_2_supervisor: applicant?.reference2?.supervisor_name ?? '',
      work_reference_2_phone: applicant?.reference2?.telephone ?? '',
      work_reference_2_verified: verified_check_2 ? 1 : 0,
      work_reference_2_notes: data.ref_2_notes ?? '',
      work_reference_2_date_verified: data.verified_2_date?._d
        ? moment(data.verified_2_date._d).format(DATE_FORMAT)
        : '',

      work_reference_5_employer: applicant?.reference3?.last_employee ?? '',
      work_reference_5_position: applicant?.reference3?.position ?? '',
      work_reference_5_supervisor: applicant?.reference3?.supervisor_name ?? '',
      work_reference_5_phone: applicant?.reference3?.telephone ?? '',
      work_reference_5_verified: verified_check_5 ? 1 : 0,
      work_reference_5_notes: data.ref_5_notes ?? '',
      work_reference_5_date_verified: data.verified_5_date?._d
        ? moment(data.verified_5_date._d).format(DATE_FORMAT)
        : '',

      work_reference_6_employer: data.work_reference_6_employer ?? '',
      work_reference_6_position: data.work_reference_6_position ?? '',
      work_reference_6_supervisor: data.work_reference_6_supervisor ?? '',
      work_reference_6_phone: data.work_reference_6_phone ?? '',
      work_reference_6_verified: verified_check_6 ? 1 : 0,
      work_reference_6_notes: data.ref_5_notes ?? '',
      work_reference_6_date_verified: data.verified_6_date?._d
        ? moment(data.verified_6_date._d).format(DATE_FORMAT)
        : '',

      work_reference_one_question_1: data.hire_1 ?? '',
      work_reference_one_question_2: data.work_ethic_1 ?? '',
      work_reference_one_question_3: data.consistency_1 ?? '',
      work_reference_two_question_1: data.hire_2 ?? '',
      work_reference_two_question_2: data.work_ethic_2 ?? '',
      work_reference_two_question_3: data.consistency_2 ?? '',
      hire_5: data.hire_5 ?? '',
      hire_6: data.hire_6 ?? '',
      consistency_5: data.consistency_5 ?? '',
      consistency_6: data.consistency_6 ?? '',
      work_ethic_5: data.work_ethic_5 ?? '',
      work_ethic_6: data.work_ethic_6 ?? '',

      confirm_dates_1: data.confirm_dates_1 ?? '',
      confirm_dates_2: data.confirm_dates_2 ?? '',
      confirm_dates_5: data.confirm_dates_5 ?? '',
      confirm_dates_6: data.confirm_dates_6 ?? '',
      key_strengths_1: data.key_strengths_1 ?? '',
      key_strengths_2: data.key_strengths_2 ?? '',
      key_strengths_5: data.key_strengths_5 ?? '',
      key_strengths_6: data.key_strengths_6 ?? '',
      prior_knowledge_1: data.prior_knowledge_1 ?? '',
      prior_knowledge_2: data.prior_knowledge_2 ?? '',
      prior_knowledge_5: data.prior_knowledge_5 ?? '',
      prior_knowledge_6: data.prior_knowledge_6 ?? '',
      reason_good_fit_1: data.reason_good_fit_1 ?? '',
      reason_good_fit_2: data.reason_good_fit_2 ?? '',
      reason_good_fit_5: data.reason_good_fit_5 ?? '',
      reason_good_fit_6: data.reason_good_fit_6 ?? '',
      facebook_check: data.facebook_check ? 1 : 0,
      facebook_date: data.facebook_date
        ? moment(data.facebook_date).format(DATE_FORMAT)
        : '',
      instagram_check: data.instagram_check ? 1 : 0,
      instagram_date: data.instagram_date
        ? moment(data.instagram_date).format(DATE_FORMAT)
        : '',
      social_media_notes: data.desc ?? '',
    };

    const requestData = {
      applicant_id: hash,
      date_of_interview:
        applicant?.date_of_interview || moment().format(DATE_FORMAT),
      date_of_submission: moment().format(DATE_FORMAT),
      interviewer: user?.id,
      status: 2,
      form_id: formId,
      form: form,
    };

    dispatch(interviewActions.saveInterviewForm(requestData)).then((res) => {
      if (res.success) {
        setFormId(res.data?.form_id);
        setNewFormId(res.data?.form_id);
        setKey('interviewForm');
        setIsNew(false);
        setIsSubmittable(isFormFilled(form));
        return;
      } else {
        toast.error(res.message);
      }
    });
    // }
    setLoading(false);
  };

  const onCancelClick = () => {
    setCurrentView('listView');
    setIsNew(false);
  };

  const verified_check_1 = watch('verified_check_1');
  const verified_check_2 = watch('verified_check_2');
  const verified_check_5 = watch('verified_check_5');
  const verified_check_6 = watch('verified_check_6');

  useEffect(() => {
    if (applicant && interviewForm) {
      const form = {
        work_reference_1_employer: applicant?.reference1?.last_employee ?? '',
        work_reference_1_position: applicant?.reference1?.position ?? '',
        work_reference_1_supervisor:
          applicant?.reference1?.supervisor_name ?? '',
        work_reference_1_phone: applicant?.reference1?.telephone ?? '',
        work_reference_1_verified: verified_check_1 ? 1 : 0,
        work_reference_1_notes:
          interviewForm?.form?.work_reference_1_notes ?? '',
        work_reference_1_date_verified: interviewForm?.form
          ?.work_reference_1_date_verified
          ? moment(interviewForm?.form?.verified_1_date).format(DATE_FORMAT)
          : '',
        work_reference_2_employer: applicant?.reference2?.last_employee ?? '',
        work_reference_2_position: applicant?.reference2?.position ?? '',
        work_reference_2_supervisor:
          applicant?.reference2?.supervisor_name ?? '',
        work_reference_2_phone: applicant?.reference2?.telephone ?? '',
        work_reference_2_verified: verified_check_2 ? 1 : 0,
        work_reference_2_notes:
          interviewForm?.form?.work_reference_2_notes ?? '',
        work_reference_2_date_verified: interviewForm?.form
          ?.work_reference_2_date_verified
          ? moment(interviewForm?.form?.work_reference_2_date_verified).format(
              DATE_FORMAT,
            )
          : '',
        work_reference_5_employer: applicant?.reference3?.last_employee ?? '',
        work_reference_5_position: applicant?.reference3?.position ?? '',
        work_reference_5_supervisor:
          applicant?.reference3?.supervisor_name ?? '',
        work_reference_5_phone: applicant?.reference3?.telephone ?? '',
        work_reference_5_verified: verified_check_5 ? 1 : 0,
        work_reference_5_notes:
          interviewForm?.form?.work_reference_5_notes ?? '',
        work_reference_5_date_verified: interviewForm?.form
          ?.work_reference_5_date_verified
          ? moment(interviewForm?.form?.work_reference_5_date_verified).format(
              DATE_FORMAT,
            )
          : '',

        work_reference_6_employer:
          interviewForm?.form?.work_reference_6_employer ?? '',
        work_reference_6_position:
          interviewForm?.form?.work_reference_6_position ?? '',
        work_reference_6_supervisor:
          interviewForm?.form?.work_reference_6_supervisor ?? '',
        work_reference_6_phone:
          interviewForm?.form?.work_reference_6_phone ?? '',
        work_reference_6_verified: verified_check_6 ? 1 : 0,
        work_reference_6_notes:
          interviewForm?.form?.work_reference_6_notes ?? '',
        work_reference_6_date_verified: interviewForm?.form
          ?.work_reference_6_date_verified
          ? moment(interviewForm?.form?.work_reference_6_date_verified).format(
              DATE_FORMAT,
            )
          : '',
        work_reference_one_question_1:
          interviewForm?.form?.work_reference_one_question_1 ?? '',
        work_reference_one_question_2:
          interviewForm?.form?.work_reference_one_question_2 ?? '',
        work_reference_one_question_3:
          interviewForm?.form?.work_reference_one_question_3 ?? '',
        work_reference_two_question_1:
          interviewForm?.form?.work_reference_two_question_1 ?? '',
        work_reference_two_question_2:
          interviewForm?.form?.work_reference_two_question_2 ?? '',
        work_reference_two_question_3:
          interviewForm?.form?.work_reference_two_question_3 ?? '',
        hire_5: interviewForm?.form?.hire_5 ?? '',
        hire_6: interviewForm?.form?.hire_6 ?? '',
        consistency_5: interviewForm?.form?.consistency_5 ?? '',
        consistency_6: interviewForm?.form?.consistency_6 ?? '',
        work_ethic_5: interviewForm?.form?.work_ethic_5 ?? '',
        work_ethic_6: interviewForm?.form?.work_ethic_6 ?? '',
        confirm_dates_5: interviewForm?.form?.confirm_dates_5 ?? '',
        confirm_dates_6: interviewForm?.form?.confirm_dates_6 ?? '',
        key_strengths_5: interviewForm?.form?.key_strengths_5 ?? '',
        key_strengths_6: interviewForm?.form?.key_strengths_6 ?? '',
        prior_knowledge_5: interviewForm?.form?.prior_knowledge_5 ?? '',
        prior_knowledge_6: interviewForm?.form?.prior_knowledge_6 ?? '',
        reason_good_fit_5: interviewForm?.form?.reason_good_fit_5 ?? '',
        reason_good_fit_6: interviewForm?.form?.reason_good_fit_6 ?? '',

        facebook_check: interviewForm?.form?.facebook_check ? 1 : 0,
        facebook_date: interviewForm?.form?.facebook_date
          ? moment(interviewForm?.form?.facebook_date).format(DATE_FORMAT)
          : '',
        instagram_check: interviewForm?.form?.instagram_check ? 1 : 0,
        instagram_date: interviewForm?.form?.instagram_date
          ? moment(interviewForm?.form?.instagram_date).format(DATE_FORMAT)
          : '',
        social_media_notes: interviewForm?.form?.social_media_notes ?? '',
      };
      setIsSubmittable(isFormFilled(form));
    }
  }, [interviewForm]);

  useEffect(() => {
    // if isNew, user don't have access to only referenceForm first.
    if (isNew) {
      setIsEnabled(true);
      return;
    }
    setIsEnabled(false);
  }, [isNew]);

  useEffect(() => {
    if (
      formId &&
      interviewForm?.version &&
      interviewForm?.version != latestInterviewFormVersion &&
      !hasWarnedRef.current
    ) {
      hasWarnedRef.current = true;
      toast.error(
        'A new version of interview form has been released. Please create a new interview form for this applicant.',
      );
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
    if (interviewForm?.form && !isNew) {
      reset({
        work_reference_6_employer:
          interviewForm?.form?.work_reference_6_employer ?? '',
        work_reference_6_position:
          interviewForm?.form?.work_reference_6_position ?? '',
        work_reference_6_supervisor:
          interviewForm?.form?.work_reference_6_supervisor ?? '',
        work_reference_6_phone:
          interviewForm?.form?.work_reference_6_phone ?? '',
        verified_check_1: interviewForm?.form?.work_reference_1_verified ?? '',
        ref_1_notes: interviewForm?.form?.work_reference_1_notes ?? '',
        verified_1_date: interviewForm?.form?.work_reference_1_date_verified
          ? moment(interviewForm.form.work_reference_1_date_verified)
          : '',
        verified_check_2: interviewForm?.form?.work_reference_2_verified ?? '',
        ref_2_notes: interviewForm?.form?.work_reference_2_notes ?? '',
        verified_2_date: interviewForm?.form?.work_reference_2_date_verified
          ? moment(interviewForm.form.work_reference_2_date_verified)
          : '',
        verified_check_5: interviewForm?.form?.work_reference_5_verified ?? '',
        ref_5_notes: interviewForm?.form?.work_reference_5_notes ?? '',
        verified_5_date: interviewForm?.form?.work_reference_5_date_verified
          ? moment(interviewForm.form.work_reference_5_date_verified)
          : '',
        verified_check_6: interviewForm?.form?.work_reference_6_verified ?? '',
        ref_6_notes: interviewForm?.form?.work_reference_6_notes ?? '',
        verified_6_date: interviewForm?.form?.work_reference_6_date_verified
          ? moment(interviewForm.form.work_reference_6_date_verified)
          : '',

        hire_1: interviewForm?.form?.work_reference_one_question_1 ?? '',
        work_ethic_1: interviewForm?.form?.work_reference_one_question_2 ?? '',
        consistency_1: interviewForm?.form?.work_reference_one_question_3 ?? '',
        hire_2: interviewForm?.form?.work_reference_two_question_1 ?? '',
        work_ethic_2: interviewForm?.form?.work_reference_two_question_2 ?? '',
        consistency_2: interviewForm?.form?.work_reference_two_question_3 ?? '',

        hire_5: interviewForm?.form?.hire_5 ?? '',
        work_ethic_5: interviewForm?.form?.work_ethic_5 ?? '',
        consistency_5: interviewForm?.form?.consistency_5 ?? '',

        hire_6: interviewForm?.form?.hire_6 ?? '',
        work_ethic_6: interviewForm?.form?.work_ethic_6 ?? '',
        consistency_6: interviewForm?.form?.consistency_6 ?? '',

        facebook_check: interviewForm?.form?.facebook_check ?? '',
        facebook_date: interviewForm?.form?.facebook_date
          ? moment(interviewForm.form.facebook_date)
          : '',
        instagram_check: interviewForm?.form?.instagram_check ?? '',
        instagram_date: interviewForm?.form?.instagram_date
          ? moment(interviewForm.form.instagram_date)
          : '',
        confirm_dates_1: interviewForm?.form?.confirm_dates_1 ?? '',
        confirm_dates_2: interviewForm?.form?.confirm_dates_2 ?? '',
        confirm_dates_5: interviewForm?.form?.confirm_dates_5 ?? '',
        confirm_dates_6: interviewForm?.form?.confirm_dates_6 ?? '',
        key_strengths_1: interviewForm?.form?.key_strengths_1 ?? '',
        key_strengths_2: interviewForm?.form?.key_strengths_2 ?? '',
        key_strengths_5: interviewForm?.form?.key_strengths_5 ?? '',
        key_strengths_6: interviewForm?.form?.key_strengths_6 ?? '',
        prior_knowledge_1: interviewForm?.form?.prior_knowledge_1 ?? '',
        prior_knowledge_2: interviewForm?.form?.prior_knowledge_2 ?? '',
        prior_knowledge_5: interviewForm?.form?.prior_knowledge_5 ?? '',
        prior_knowledge_6: interviewForm?.form?.prior_knowledge_6 ?? '',
        reason_good_fit_1: interviewForm?.form?.reason_good_fit_1 ?? '',
        reason_good_fit_2: interviewForm?.form?.reason_good_fit_2 ?? '',
        reason_good_fit_5: interviewForm?.form?.reason_good_fit_5 ?? '',
        reason_good_fit_6: interviewForm?.form?.reason_good_fit_6 ?? '',
        desc: interviewForm?.form?.social_media_notes ?? '',
      });
    }
  }, [interviewForm]);

  const isFormFilled = (obj) => {
    for (const key in obj) {
      if (
        [
          'work_reference_6_employer',
          'work_reference_6_position',
          'work_reference_6_supervisor',
          'work_reference_6_phone',
          'work_reference_6_verified',
          'work_reference_6_date_verified',
          'work_reference_6_notes',
          'hire_6',
          'consistency_6',
          'work_ethic_6',
          'confirm_dates_6',
          'key_strengths_6',
          'prior_knowledge_6',
          'reason_good_fit_6',
        ].includes(key)
      ) {
        continue;
      }
      if (
        (obj.hasOwnProperty(key) &&
          (obj[key] === null || obj[key] === undefined || obj[key] === '')) ||
        obj[key] === 0
      ) {
        return false; // If any key has a null, undefined, or empty string value, return false
      }
    }
    return true; // If all keys have a value, return true
  };

  return (
    <>
      <Tabs
        id='controlled-tab-example'
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className='mb-3 interview_form_wrapper'
      >
        <Tab eventKey='referenceForm' title='Reference Form'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='ref_Form_wrapper'>
              <h3>Work References</h3>
              <p className='note_para'>
                <b>Note:</b> It is mandatory to call and verify all the
                references.
                <div>
                  {!isRefsOk && (
                    <p className='refs_not_ok'>
                      Please fill all the reference details (except Reference #4
                      marked as optional) for submitting the interview form.
                      Please edit the profile OR edit the job application for
                      entering the reference details.{' '}
                    </p>
                  )}
                </div>
              </p>{' '}
              <div></div>
              <ReferenceContainer
                references={[
                  { reference: applicant?.reference1, position: 1 },
                  { reference: applicant?.reference2, position: 2 },
                  { reference: applicant?.reference3, position: 5 },
                  { reference: applicant?.reference4, position: 6 },
                ]}
                register={register}
                control={control}
                errors={errors}
              />
              <Row key='reference-6' className='show-grid interview__grid'>
                <div className='interview__head'>
                  <h1>Reference #4 (Optional)</h1>
                </div>

                <Col className='col_table'>
                  <div className='interview__wrap'>
                    <table className='ref_table'>
                      <tbody>
                        <tr key='work_reference_6_employer'>
                          <td>Employer</td>
                          <td>
                            <input
                              type='text'
                              placeholder='Employer'
                              {...register('work_reference_6_employer')}
                              className={`${errors?.work_reference_6_employer?.message ? 'error' : ''}`}
                            />
                          </td>
                        </tr>

                        <tr key='work_reference_6_position'>
                          <td>Position Held</td>
                          <td>
                            <input
                              type='text'
                              placeholder='Position'
                              {...register('work_reference_6_position')}
                              className={`${errors?.work_reference_6_position?.message ? 'error' : ''}`}
                            />
                          </td>
                        </tr>

                        <tr key='work_reference_6_supervisor'>
                          <td>Supervisor</td>
                          <td>
                            <input
                              type='text'
                              placeholder='Supervisor'
                              {...register('work_reference_6_supervisor')}
                              className={`${errors?.work_reference_6_supervisor?.message ? 'error' : ''}`}
                            />
                          </td>
                        </tr>

                        <tr key='work_reference_6_phone'>
                          <td>Cell Phone</td>
                          <td>
                            <Controller
                              name='work_reference_6_phone'
                              control={control}
                              rules={{
                                required: 'Cell Phone is required',
                                minLength: {
                                  value: 10,
                                  message: 'Cell Phone must be 10 digits long',
                                },
                              }} 
                              render={({
                                field: { onChange, onBlur, value, ref },
                              }) => (
                                <NumberFormat
                                  format='###-###-####'
                                  mask='_'
                                  placeholder='Phone'
                                  className={
                                    errors?.work_reference_6_phone?.message
                                      ? 'profile-form-field-error'
                                      : ''
                                  }
                                  isNumericString
                                  value={value}
                                  onValueChange={({ value }) => onChange(value)}
                                  onBlur={onBlur}
                                  getInputRef={ref}
                                  onKeyDown={preventNegativeValueInDocs}
                                />
                              )}
                              />
                          </td>
                        </tr>

                        <tr>
                          <td>Verified</td>
                          <td>
                            <div className='checkbox_wrap'>
                              <input
                                type='checkbox'
                                {...register(`verified_check_6`)}
                                className={`checkbox_input ${
                                  errors?.[`verified_check_6`]?.message
                                    ? 'error'
                                    : ''
                                }`}
                              />
                              <label className='checkbox_label'></label>
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>Verified Date</td>
                          <td>
                            <div className='verified_date'>
                              <Controller
                                control={control}
                                name={`verified_6_date`}
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <DatePicker
                                    className='datepicker_ref'
                                    placeholderText='Date'
                                    ref={ref}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    selected={value}
                                    showMonthDropdown
                                    showYearDropdown
                                    maxDate={moment(new Date())}
                                    customInput={<CustomDateInput />}
                                  />
                                )}
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>

                <ReferenceQuestionnaire
                  position='6'
                  register={register}
                  errors={errors}
                />
              </Row>
              <div className='referenceCheckSection social_media'>
                <h4>Social Media Check</h4>
                <Row className='show-grid'>
                  <Col>
                    <div className='checkbox_wrap'>
                      <p>Facebook</p>
                      <input
                        type='checkbox'
                        {...register('facebook_check')}
                        className={`checkbox_input ${
                          errors?.facebook_check?.message ? 'error' : ''
                        }`}
                      />{' '}
                      <label className='checkbox_label'></label>
                    </div>
                    <div className='facebook_date'>
                      <Controller
                        control={control}
                        name='facebook_date'
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <DatePicker
                            // className="datepicker_ref"
                            {...register('facebook_date')}
                            className={`${
                              errors.facebook_date?.message ? 'error' : ''
                            }`}
                            ref={ref}
                            placeholderText='Date'
                            onChange={onChange}
                            onBlur={onBlur}
                            selected={value}
                            showMonthDropdown
                            showYearDropdown
                            maxDate={moment(new Date())}
                            customInput={<CustomDateInput />}
                          />
                        )}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className='checkbox_wrap'>
                      <p>Instagram</p>
                      <input
                        type='checkbox'
                        {...register('instagram_check')}
                        className={`checkbox_input ${
                          errors?.instagram_check?.message ? 'error' : ''
                        }`}
                      />{' '}
                      <label className='checkbox_label'></label>
                    </div>
                    <div className='facebook_date'>
                      <Controller
                        control={control}
                        name='instagram_date'
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <DatePicker
                            className={`${
                              errors.instagram_date?.message ? 'error' : ''
                            }`}
                            {...register('instagram_date')}
                            ref={ref}
                            placeholderText='Date'
                            onChange={onChange}
                            onBlur={onBlur}
                            selected={value}
                            showMonthDropdown
                            showYearDropdown
                            maxDate={moment(new Date())}
                            customInput={<CustomDateInput />}
                          />
                        )}
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={0}>
                    <p>Notes</p>
                    <textarea
                      className={`${errors.desc?.message ? 'error' : ''}`}
                      id=''
                      name=''
                      rows='5'
                      cols='33'
                      {...register('desc')}
                    />
                  </Col>
                </Row>
              </div>
              <div className='ref_form_btn'>
                <div className='interview_btn'>
                  <button className='button' onClick={onCancelClick}>
                    {' '}
                    Cancel{' '}
                  </button>
                  <button className='button' type='submit' disabled={loading}>
                    {' '}
                    Next{' '}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Tab>
        <Tab
          eventKey='interviewForm'
          title='Interview Form'
          disabled={isEnabled}
        >
          <InterviewForm
            hash={props.hash}
            applicant={applicant}
            setCurrentView={setCurrentView}
            editMode={props.editMode}
            user={user}
            formId={props.formId}
            isNew={isNew}
            setIsNew={setIsNew}
            setKey={setKey}
            getInterviewFormList={props.getInterviewFormList}
            newFormId={newFormId}
            isSubmittable={isSubmittable}
          />
        </Tab>
      </Tabs>
    </>
  );
};

ReferenceForm.propTypes = {
  hash: PropTypes.string,
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFormId: PropTypes.func,
  user: PropTypes.object,
  applicant: PropTypes.object,
  setCurrentView: PropTypes.func,
  isNew: PropTypes.bool,
  setIsNew: PropTypes.func,
  newFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setNewFormId: PropTypes.func,
  setIsSubmittable: PropTypes.func,
  isSubmittable: PropTypes.bool
};

export default ReferenceForm;
